import { Component, TemplateRef, ViewChild } from '@angular/core';
import { MatChipListboxChange } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';
import { ColleagueService } from '../../../core/services/colleague.service';
import { BaseComponent } from './../../../core/components/base/base.component';
import { NETWORK_SPECIALITIES, TITLES } from './../../../core/data/app.data';
import { FeedbackService } from './../../../core/services/feedback.service';

const COLLEAGUE_VIEW_MODE_KEY = 'colleagueSettingsViewMode';

/**
 * The three panels of the MedicAI Network tab.
 *
 * `all` is the original COLLEAGUES panel and must behave exactly as it did before the theatre
 * roster existed. `anaesthetists` and `assistants` are the same list narrowed by a ROLE FLAG —
 * never by speciality. See `tasks/theatre-booking-and-procedure-lifecycle.md` §5.1: a practice's
 * network accumulates every anaesthetist they have ever written a letter to, while the theatre
 * picker wants the three they actually work with. Flags express curation; speciality expresses
 * fact. Speciality is used below as a DEFAULT when `isAnaesthetist` is ticked, never as the filter.
 */
export type ColleagueRoster = 'all' | 'anaesthetists' | 'assistants';

/**
 * The exclude list for the SURGICAL ASSISTANTS panel (F2).
 *
 * Deliberately an `exclude` list of specialities that clearly never scrub an assistant, NOT an
 * `include` list of "surgical" specialities. Gastroenterology, cardiology and interventional
 * radiology all bill surgical assistants, so an include list of the obvious surgical disciplines
 * would silently hide the panel from practices that need it — and the only symptom would be a
 * support ticket. Every entry here is a discipline with no theatre list at all.
 *
 * Values are `SPECIALITIES[].value` from `client/src/app/core/data/app.data.ts` and are matched
 * against `Group.groupSpeciality` by `appGroupSpecialtyHideStructural`.
 */
export const NON_ASSISTANT_SPECIALITIES: string[] = [
  'Biokineticist',
  'Chiropractor',
  'Dietitian',
  'Occupational Therapist',
  'Pathology',
  'Pharmacist',
  'Physiotherapy',
  'Psychiatry',
  'Psychology',
  'Social Worker',
  'Speech Therapy',
];

/**
 * What a contact is, as the one COLLEAGUES & THEATRE TEAM section shows it (owner, 2026-09-22): its chip colour, icon and
 * filter. Roles stay independent flags; this is the MAIN one, anaesthetist first, then surgical assistant, and a person
 * with neither is a colleague you write to.
 */
export type ContactKind = 'colleague' | 'anaesthetist' | 'assistant';
export type ContactFilter = 'all' | ContactKind;

/** Speciality pre-filled when `isAnaesthetist` is ticked and the field is still empty. */
const DEFAULT_ANAESTHETIST_SPECIALITY = 'Anesthetics';

@Component({
  selector: 'app-colleague-settings',
  templateUrl: './colleague-settings.component.html',
  styleUrls: ['./colleague-settings.component.scss']
})
export class ColleagueSettingsComponent extends BaseComponent {
  colleagues: any[] = []; // Array to store colleague objects
  editingColleague: any = {}; // Object to store the new colleague being added
  @ViewChild('addColleagueDialog') addColleagueDialog?: TemplateRef<any>;

  colleagueViewMode: 'chips' | 'grid' = 'chips';

  titles = TITLES;
  specialities = NETWORK_SPECIALITIES;
  nonAssistantSpecialities = NON_ASSISTANT_SPECIALITIES;

  /**
   * Search text and page position, held PER PANEL.
   *
   * Sharing one search box across three panels would mean typing in ANAESTHETISTS silently
   * filtered COLLEAGUES too. `all` is the original state; `colleagueSearchQuery` and
   * `listPageIndex` below still read and write it, so the COLLEAGUES panel behaves exactly as it
   * did before the other two existed.
   */
  private rosterSearch: Record<ColleagueRoster, string> = { all: '', anaesthetists: '', assistants: '' };
  private rosterPage: Record<ColleagueRoster, number> = { all: 0, anaesthetists: 0, assistants: 0 };

  /** Unchanged accessor for the COLLEAGUES panel's search text. */
  get colleagueSearchQuery(): string {
    return this.rosterSearch.all;
  }
  set colleagueSearchQuery(value: string) {
    this.rosterSearch.all = value;
  }

  searchQueryFor(roster: ColleagueRoster): string {
    return this.rosterSearch[roster] ?? '';
  }

  /** Typing always returns to the first page — the pre-existing `listPageIndex = 0` behaviour. */
  setSearchQueryFor(roster: ColleagueRoster, value: string): void {
    this.rosterSearch[roster] = value ?? '';
    this.rosterPage[roster] = 0;
  }

  clearSearchFor(roster: ColleagueRoster): void {
    this.setSearchQueryFor(roster, '');
  }

  /**
   * Does this colleague belong in the given panel?
   *
   * Truthy rather than `=== true`: MSSQL `bit` arrives as a boolean through Sequelize but as
   * `0`/`1` through some JSON paths, and a colleague created before F1 has whatever the column
   * default supplied. `all` never filters.
   */
  private matchesRoster(colleague: any, roster: ColleagueRoster): boolean {
    if (!colleague) return false;
    if (roster === 'anaesthetists') return !!colleague.isAnaesthetist;
    if (roster === 'assistants') return !!colleague.isSurgicalAssistant;
    return true;
  }

  /**
   * The panel's list: role filter, then the same free-text search and surname sort the COLLEAGUES
   * panel has always used. Search fields are deliberately identical across all three panels.
   */
  filteredFor(roster: ColleagueRoster): any[] {
    const q = (this.searchQueryFor(roster) || '').trim().toLowerCase();
    const scoped = (this.colleagues || []).filter((c: any) => this.matchesRoster(c, roster));
    const list = !q
      ? scoped
      : scoped.filter((c: any) => {
          const name = `${c.title || ''} ${c.name || ''} ${c.surname || ''}`.toLowerCase();
          const speciality = (c.speciality || '').toLowerCase();
          const email = (c.email || '').toLowerCase();
          return name.includes(q) || speciality.includes(q) || email.includes(q);
        });
    return [...list].sort((a: any, b: any) => {
      const sa = `${(a.surname || '').toLowerCase()} ${(a.name || '').toLowerCase()}`;
      const sb = `${(b.surname || '').toLowerCase()} ${(b.name || '').toLowerCase()}`;
      return sa.localeCompare(sb, undefined, { sensitivity: 'base' });
    });
  }

  /**
   * The component's pre-existing public surface, retained as `'all'` aliases rather than deleted.
   * The template now calls the roster-parameterised forms, but these are what anything outside
   * this file (a spec, the F4 booking picker) already knows about, and preserving them is what
   * makes this change additive.
   */
  get filteredColleagues(): any[] {
    return this.filteredFor('all');
  }

  listPageSize = 20;

  get listPageIndex(): number {
    return this.rosterPage.all;
  }
  set listPageIndex(value: number) {
    this.rosterPage.all = value;
  }

  pageIndexFor(roster: ColleagueRoster): number {
    return this.rosterPage[roster] ?? 0;
  }

  totalPagesFor(roster: ColleagueRoster): number {
    const n = this.filteredFor(roster).length;
    return n ? Math.ceil(n / this.listPageSize) : 1;
  }

  effectivePageIndexFor(roster: ColleagueRoster): number {
    const max = this.totalPagesFor(roster) - 1;
    return Math.min(Math.max(0, this.pageIndexFor(roster)), max);
  }

  pagedFor(roster: ColleagueRoster): any[] {
    const list = this.filteredFor(roster);
    const start = this.effectivePageIndexFor(roster) * this.listPageSize;
    return list.slice(start, start + this.listPageSize);
  }

  hasPrevPageFor(roster: ColleagueRoster): boolean {
    return this.pageIndexFor(roster) > 0;
  }

  hasNextPageFor(roster: ColleagueRoster): boolean {
    return this.pageIndexFor(roster) < this.totalPagesFor(roster) - 1;
  }

  prevPageFor(roster: ColleagueRoster): void {
    if (this.hasPrevPageFor(roster)) this.rosterPage[roster] = this.pageIndexFor(roster) - 1;
  }

  nextPageFor(roster: ColleagueRoster): void {
    if (this.hasNextPageFor(roster)) this.rosterPage[roster] = this.pageIndexFor(roster) + 1;
  }

  get listTotalPages(): number {
    return this.totalPagesFor('all');
  }

  get effectiveListPageIndex(): number {
    return this.effectivePageIndexFor('all');
  }

  get pagedColleagues(): any[] {
    return this.pagedFor('all');
  }

  get listHasPrevPage(): boolean {
    return this.hasPrevPageFor('all');
  }

  get listHasNextPage(): boolean {
    return this.hasNextPageFor('all');
  }

  listPrevPage(): void {
    this.prevPageFor('all');
  }

  listNextPage(): void {
    this.nextPageFor('all');
  }

  /** Panel wording. Kept in one place so the three panels cannot drift apart. */
  rosterNoun(roster: ColleagueRoster): string {
    if (roster === 'anaesthetists') return 'anaesthetists';
    if (roster === 'assistants') return 'surgical assistants';
    return 'colleagues';
  }

  rosterTitle(roster: ColleagueRoster): string {
    if (roster === 'anaesthetists') return 'Anaesthetists';
    if (roster === 'assistants') return 'Surgical Assistants';
    return 'Colleagues';
  }

  rosterEmptyHint(roster: ColleagueRoster): string {
    if (this.searchQueryFor(roster)) return `No ${this.rosterNoun(roster)} match your search.`;
    if (roster === 'anaesthetists') {
      return 'No anaesthetists yet. Add one with the icon above, or tick "Anaesthetist" on an '
        + 'existing colleague — the same person can be both.';
    }
    if (roster === 'assistants') {
      return 'No surgical assistants yet. Add one with the icon above, or tick "Surgical assistant" '
        + 'on an existing colleague — the same person can be both.';
    }
    return 'No colleagues yet. Use the add icon above to add one.';
  }

  /* ── ONE section (owner, 2026-09-22) ─────────────────────────────────────────────────────────────── */

  readonly kindLabel: Record<ContactKind, string> = { colleague: 'Colleague', anaesthetist: 'Anaesthetist', assistant: 'Surgical assistant' };
  readonly kindIcon: Record<ContactFilter, string> = { all: 'groups', colleague: 'person', anaesthetist: 'monitor_heart', assistant: 'content_cut' };
  /** What each kind is for, under the dialog's kind picker: what is required, and why. */
  readonly kindHint: Record<ContactKind, string> = {
    colleague: 'Someone you write letters to. Needs an email address so MedicAI can send them letters.',
    anaesthetist: 'On your theatre roster: you book them on theatre lists. An email address is optional.',
    assistant: 'On your theatre roster and billed on claims, so add their BHF practice number. An email address is optional.',
  };
  private readonly filterLabel: Record<ContactFilter, string> = {
    all: 'All', colleague: 'Colleagues', anaesthetist: 'Anaesthetists', assistant: 'Surgical assistants',
  };
  /** Search appears once the list is long enough to need it (or while a search is typed). */
  readonly searchFrom = 8;

  activeKind: ContactFilter = 'all';
  /** The dialog's kind picker; `isNewContact` says whether it adds or edits. */
  dialogKind: ContactKind = 'colleague';
  isNewContact = false;

  kindOf(colleague: any): ContactKind {
    if (colleague?.isAnaesthetist) return 'anaesthetist';
    if (colleague?.isSurgicalAssistant) return 'assistant';
    return 'colleague';
  }

  /** A filter is a ROLE: a person who is both an anaesthetist and an assistant shows under both. */
  private matchesKind(colleague: any, kind: ContactFilter): boolean {
    if (kind === 'all') return true;
    if (kind === 'anaesthetist') return !!colleague?.isAnaesthetist;
    if (kind === 'assistant') return !!colleague?.isSurgicalAssistant;
    return !colleague?.isAnaesthetist && !colleague?.isSurgicalAssistant;
  }

  /**
   * The role chips and their counts. Worked out again only when the colleagues or the chosen role change: an imported
   * network has hundreds of people, and the template reads this on every change-detection pass.
   */
  get kindFilterOptions(): { key: ContactFilter; label: string; count: number }[] {
    const cached = this.filterOptionsCache;
    if (cached && cached.colleagues === this.colleagues && cached.kind === this.activeKind) return cached.options;
    const options = this.workOutKindFilterOptions();
    this.filterOptionsCache = { colleagues: this.colleagues, kind: this.activeKind, options };
    return options;
  }

  private filterOptionsCache?: { colleagues: any[]; kind: ContactFilter; options: { key: ContactFilter; label: string; count: number }[] };

  private workOutKindFilterOptions(): { key: ContactFilter; label: string; count: number }[] {
    const all = this.colleagues || [];
    const kinds: ContactKind[] = ['colleague', 'anaesthetist', 'assistant'];
    return [
      { key: 'all' as ContactFilter, label: this.filterLabel.all, count: all.length },
      ...kinds
        .map((key) => ({ key: key as ContactFilter, label: this.filterLabel[key], count: all.filter((c) => this.matchesKind(c, key)).length }))
        .filter((f) => f.count > 0 || f.key === this.activeKind),
    ];
  }

  setKindFilter(event: MatChipListboxChange | ContactFilter | undefined): void {
    const value = typeof event === 'string' || event === undefined ? event : (event.value as ContactFilter | undefined);
    this.activeKind = value || 'all';
    // Tapping the chosen chip again deselects it: that means All, so All shows as chosen.
    if (!value && event && typeof event !== 'string') event.source.value = 'all';
    this.rosterPage.all = 0;
  }

  get activeKindLabel(): string {
    return this.activeKind === 'all' ? 'Colleagues & theatre team' : this.filterLabel[this.activeKind];
  }

  get showSearch(): boolean {
    return (this.colleagues || []).length > this.searchFrom || !!this.colleagueSearchQuery;
  }

  /**
   * The contacts shown: the kind filter over the same search and surname sort the Colleagues panel always had. Sorted
   * again only when the colleagues, the search or the chosen role change, as the template reads it on every pass.
   */
  get visibleContacts(): any[] {
    const cached = this.visibleCache;
    const search = this.colleagueSearchQuery;
    if (cached && cached.colleagues === this.colleagues && cached.search === search && cached.kind === this.activeKind) {
      return cached.list;
    }
    const list = this.filteredFor('all').filter((c) => this.matchesKind(c, this.activeKind));
    this.visibleCache = { colleagues: this.colleagues, search, kind: this.activeKind, list };
    return list;
  }

  private visibleCache?: { colleagues: any[]; search: string; kind: ContactFilter; list: any[] };

  /** Chips are small, so a page holds more of them than of list rows. Hundreds at once made the page lag. */
  readonly chipPageSize = 100;

  get contactPageSize(): number {
    return this.colleagueViewMode === 'chips' ? this.chipPageSize : this.listPageSize;
  }

  get contactPages(): number {
    const n = this.visibleContacts.length;
    return n ? Math.ceil(n / this.contactPageSize) : 1;
  }

  get contactPageIndex(): number {
    return Math.min(Math.max(0, this.rosterPage.all), this.contactPages - 1);
  }

  setContactPage(index: number): void {
    this.rosterPage.all = Math.min(Math.max(0, index), this.contactPages - 1);
  }

  get pagedContacts(): any[] {
    const start = this.contactPageIndex * this.contactPageSize;
    return this.visibleContacts.slice(start, start + this.contactPageSize);
  }

  get visibleEmptyHint(): string {
    if (this.colleagueSearchQuery) return 'Nobody matches your search.';
    return `No ${this.filterLabel[this.activeKind].toLowerCase()} yet.`;
  }

  clearContactFilters(event?: Event): void {
    event?.preventDefault();
    this.activeKind = 'all';
    this.clearSearchFor('all');
  }

  fullName(colleague: any): string {
    return [colleague?.title, colleague?.name, colleague?.surname].filter(Boolean).join(' ');
  }

  /** "Anaesthetist, Surgical assistant" or "Colleague", for screen readers on a chip. */
  roleSummary(colleague: any): string {
    const roles = this.roleLabels(colleague);
    return roles.length ? roles.join(', ') : this.kindLabel.colleague;
  }

  trackById = (_: number, colleague: any) => colleague?.id;
  trackByKey = (_: number, f: { key: string }) => f.key;

  /**
   * The dialog's kind picker. The kind is the MAIN role flag: Colleague clears both theatre roles, Anaesthetist or
   * Surgical assistant sets that one and clears the other (the "Also a…" box under the fields adds it back). Tapping the
   * chosen kind again would leave none chosen, so it is put back.
   */
  setDialogKind(event: MatChipListboxChange | ContactKind | undefined): void {
    const value = typeof event === 'string' || event === undefined ? event : (event.value as ContactKind | undefined);
    if (!value) {
      if (event && typeof event !== 'string') event.source.value = this.dialogKind;
      return;
    }
    this.dialogKind = value;
    const c = this.editingColleague;
    if (value === 'colleague') {
      c.isAnaesthetist = false;
      c.isSurgicalAssistant = false;
    } else if (value === 'anaesthetist') {
      c.isSurgicalAssistant = false;
      this.onAnaesthetistToggle(true);
    } else {
      c.isAnaesthetist = false;
      this.onSurgicalAssistantToggle(true);
    }
  }

  constructor(private colleagueService: ColleagueService, fs: FeedbackService, md: MatDialog) {
    super(fs, md);
    const saved = localStorage.getItem(COLLEAGUE_VIEW_MODE_KEY);
    if (saved === 'chips' || saved === 'grid') this.colleagueViewMode = saved;
    this.colleagueService.colleagues$.subscribe(colleagues => {
      if (colleagues) {
        this.colleagues = colleagues;
      }
    });
  }

  toggleColleagueViewMode(): void {
    this.colleagueViewMode = this.colleagueViewMode === 'chips' ? 'grid' : 'chips';
    localStorage.setItem(COLLEAGUE_VIEW_MODE_KEY, this.colleagueViewMode);
  }

  /**
   * @param event - The click on the panel header's add icon.
   * @param roster - Kept for callers that add straight into a role: `anaesthetists` or `assistants` pre-picks that kind.
   *   The one section's + passes nothing, and then the dialog starts on the kind the list is filtered to (Colleague
   *   when showing everyone), so a new person lands where the user was looking.
   */
  openAddColleagueDialog(event: MouseEvent, roster: ColleagueRoster = 'all'): void {
    event.preventDefault();
    event.stopPropagation();
    this.editingColleague = {
      id: this.colleagues.length + 1, // Reset the new colleague object
      isAnaesthetist: false,
      isSurgicalAssistant: false,
    };
    this.isNewContact = true;
    const kind: ContactKind = roster === 'anaesthetists' ? 'anaesthetist'
      : roster === 'assistants' ? 'assistant'
      : this.activeKind === 'all' ? 'colleague' : this.activeKind;
    this.setDialogKind(kind);
    this.dialog.open(this.addColleagueDialog!, { width: '600px' }); // Open the add colleague dialog
  }

  getTooltipText(colleague: any): string {
    const roles = this.roleLabels(colleague);
    const roleLine = roles.length ? `, Roles: ${roles.join(', ')}` : '';
    return `Speciality: ${colleague.speciality}, Email: ${colleague.email}, Contact Number: ${colleague.contactNumber || 'Not Provided'}${roleLine}`;
  }

  /** Theatre roles this person carries, for tooltips and list rows. */
  roleLabels(colleague: any): string[] {
    const labels: string[] = [];
    if (colleague && colleague.isAnaesthetist) labels.push('Anaesthetist');
    if (colleague && colleague.isSurgicalAssistant) labels.push('Surgical assistant');
    return labels;
  }

  /**
   * Ticking "Anaesthetist" DEFAULTS the speciality; it never overwrites one already chosen, and
   * un-ticking never clears it. `tasks/theatre-booking-and-procedure-lifecycle.md` §5.1: speciality
   * is a default here, never the filter.
   */
  onAnaesthetistToggle(checked: boolean): void {
    this.editingColleague.isAnaesthetist = checked;
    if (checked) this.applyAnaesthetistSpecialityDefault();
  }

  onSurgicalAssistantToggle(checked: boolean): void {
    this.editingColleague.isSurgicalAssistant = checked;
  }

  private applyAnaesthetistSpecialityDefault(): void {
    const current = (this.editingColleague && this.editingColleague.speciality) || '';
    if (!String(current).trim()) {
      this.editingColleague.speciality = DEFAULT_ANAESTHETIST_SPECIALITY;
    }
  }

  /**
   * ── F3. CONDITIONAL EMAIL, AS A FORM-LEVEL RULE ──────────────────────────────────────
   *
   * `Colleagues.email` was `NOT NULL` and is now nullable, because a practice routinely has no
   * email address for its anaesthetist. The requirement did not disappear, it became CONDITIONAL,
   * and the condition is CROSS-FIELD — it cannot be expressed as `required` on the email input,
   * because it depends on two other controls. This getter is evaluated over the whole dialog model
   * on every change-detection pass, so ticking or un-ticking either role flag re-evaluates it
   * immediately.
   *
   * Required when the person is a plain letter-recipient colleague: the letter-to-colleague send
   * path has nowhere to deliver to without one. Optional the moment either theatre role is set.
   */
  get emailIsRequired(): boolean {
    const c = this.editingColleague || {};
    return !c.isAnaesthetist && !c.isSurgicalAssistant;
  }

  /**
   * The form-level validator itself. Returns the reasons the dialog cannot be saved, in the order
   * they should be shown. Empty means valid.
   *
   * lessons.md §4, corollary — REFUSE, DO NOT COERCE. A missing email is not quietly written as
   * NULL, and neither role flag is quietly ticked to make the save legal. The save is refused and
   * the reason is stated, so the practice cannot end up believing they saved something they did
   * not.
   */
  get colleagueValidationErrors(): string[] {
    const c = this.editingColleague || {};
    const errors: string[] = [];
    const email = String(c.email || '').trim();
    if (this.emailIsRequired && !email) {
      errors.push(
        'An email address is required for a colleague you write letters to — the letter send path '
        + 'has nowhere to deliver without one. If this person is only on your theatre roster, tick '
        + 'Anaesthetist or Surgical assistant and the email becomes optional.',
      );
    }
    return errors;
  }

  get colleagueIsValid(): boolean {
    return this.colleagueValidationErrors.length === 0;
  }

  /**
   * Trims the HPCSA number, and turns a blank one into `null` rather than `''`.
   *
   * That distinction is the whole of the "validation" here, and it is not cosmetic. `''` is a
   * value the caller SUPPLIED, so the server encrypts it, stores a non-NULL ciphertext, and
   * derives a digest from it — a colleague who looks like they have an HPCSA number recorded when
   * the practice cleared the field. `null` clears both columns.
   *
   * Nothing else is validated. HPCSA numbers arrive in several spellings (`MP0123456`,
   * `0123456MP`, with or without a space) and `utils/identityHash.js normaliseHpcsa` folds them
   * all to one canonical form server-side before hashing, so the browser has no business
   * second-guessing what a practice typed.
   */
  private normaliseMpNumber(): void {
    const c = this.editingColleague || {};
    if (!('mpNumber' in c)) return;
    const trimmed = String(c.mpNumber ?? '').trim();
    c.mpNumber = trimmed.length ? trimmed : null;
  }

  editColleague(colleague: any): void {
    this.editingColleague = { ...colleague }; // Create a copy of the colleague object
    this.isNewContact = false;
    // The picker shows the person's main role; their flags are left exactly as saved.
    this.dialogKind = this.kindOf(colleague);
    this.dialog.open(this.addColleagueDialog!, { width: '600px' }); // Open the edit colleague dialog
  }

  saveColleague(): void {
    // F3 enforcement. lessons.md §4: a disabled control is not an enforcement point, so the rule
    // is applied HERE, in the handler that performs the write — the Save button stays clickable
    // and the refusal states its reason.
    // The full reason is already rendered under the Email field; the toast is the short form,
    // because `showToastMessage` shows for 3 seconds.
    if (!this.colleagueIsValid) {
      this.showToastMessage(
        'Not saved — an email address is required unless this person is an anaesthetist or '
        + 'surgical assistant.',
      );
      return; // and the dialog stays OPEN, with the entered values intact
    }

    this.normaliseMpNumber();

    const index = this.colleagues.findIndex(c => c.id === this.editingColleague.id);
    if (index !== -1) {
      const sub = this.colleagueService.updateColleague(this.editingColleague)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response: any) => {
            this.showToastMessage('Colleague updated successfully');
            sub.unsubscribe();
          },
          (error) => {
            console.error('Error updating colleague:', error);
            this.showToastMessage('There was an error updating the colleague. Please try again.');
            sub.unsubscribe();
          }
        );
    } else {
      const sub = this.colleagueService.addColleague(this.editingColleague)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response: any) => {
            this.showToastMessage('Colleague added successfully');
            sub.unsubscribe();
          },
          (error) => {
            console.error('Error adding colleague:', error);
            this.showToastMessage('There was an error adding the colleague. Please try again.');
            sub.unsubscribe();
          }
        );
    }
    this.dialog.closeAll(); // Close the edit colleague dialog
  }

  cancelEdit(): void {
    this.dialog.closeAll(); // Close the edit colleague dialog without saving changes
  }

  async removeColleague(colleague: any): Promise<void> {
    const index = this.colleagues.indexOf(colleague);
    if (index !== -1) {
      const confirmed = await this.showConfirmationDialog();

      if (confirmed) {
        const sub = this.colleagueService.deleteColleague(colleague)
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            (response: any) => {
              this.showToastMessage('Colleague deleted successfully');
              sub.unsubscribe();
            },
            (error) => {
              console.error('Error deleting colleague:', error);
              this.showToastMessage('There was an error deleting the colleague. Please try again.');
              sub.unsubscribe();
            }
          );
      }
    }
  }

}